/**
 * AutomationBinding
 *
 * A platform-independent state binding: continuous synchronization between
 * a data source and one widget property. The counterpart to event-driven
 * triggers — together they make up the widget's automation.
 *
 *   property  ←  strategy
 *
 * Strategies (see docs/Binding_Architecture_FINAL.md):
 *   'state'    — a Home Assistant entity's state or one selected attribute,
 *                with an optional transformation filter and fallback value
 *   'mapping'  — an entity's state/attribute mapped through `rules[]` to a
 *                per-widget-property result (on/off, multi-state, ranges)
 *   'template' — a raw Jinja2 template expression
 *   'constant' — a fixed value
 *   'switch'   — an entity's state/attribute compared with `cases[]` in
 *                order; the first matching case's own binding gives the
 *                value (`when: null` is the else case). Composite: the
 *                case bindings are AutomationBindings of the same property
 *
 * Generators translate bindings into their target format (the HA generator
 * emits the widget's `properties:` section) — see
 * automation/generators/bindingGenerators.js.
 *
 * `transforms[]` is an ordered, strategy-independent value-transform
 * pipeline (see automation/schema/valueTransforms.js) applied to the
 * incoming value before strategy evaluation — every strategy may use it.
 * It is part of the persisted model for the same reason `rules[]` is:
 * the editor, preview, and (later) generators must all read the same
 * definition rather than the editor holding its own transient copy.
 * Generator support for transforms is not implemented yet — see the
 * "Architecture Correction — Move Value Transforms into AutomationBinding"
 * change; only model ownership moved in that change, no generation.
 *
 * `formatter` is a presentation step applied after `transforms[]`, for
 * 'state' (and later 'aggregate') bindings only — 'mapping' is excluded,
 * since its output is always the matched rule's own literal, not a
 * retrieved value there's anything left to format. See
 * automation/schema/valueFormatters.js for the registry and
 * buildFormatIntent(); this class stores exactly the formatter it's
 * given and never inspects entityId/attribute to infer one — historical
 * data compatibility (e.g. attribute:'rgb_color' implying the Color
 * formatter) is resolved in LegacyEventsToAst.js, not here.
 *
 * This class is a flat data model by design — no generic `data` payload.
 * See docs/Binding_Architecture_FINAL.md §3 for why.
 */
export class AutomationBinding {
  /**
   * LEGACY COMPATIBILITY ONLY — `source`/`onValue`/`offValue` (and the
   * legacy `'entity'`/`'toggle'` source values) are the pre-rename field
   * names. As of the current architecture, no editor, generator, or the
   * compiler reads or writes these anymore — every live consumer uses
   * `strategy`/`rules` directly (migration/LegacyEventsToAst.js converts
   * pre-rename JSON to the current shape before it reaches this class).
   * These accessors remain only as a defensive fallback for any input
   * that constructs an AutomationBinding directly from pre-rename data
   * without going through that migration step. Remove once that is
   * confirmed to never happen — see the "Cleanup order" item in the
   * binding-architecture stabilization audit.
   */
  static #LEGACY_SOURCE_TO_STRATEGY = { entity: 'state', toggle: 'mapping', template: 'template', constant: 'constant' };
  static #STRATEGY_TO_LEGACY_SOURCE = { state: 'entity', mapping: 'toggle', template: 'template', constant: 'constant' };

  /**
   * @param {object} config
   * @param {string} [config.id]
   * @param {string} [config.property]  — widget property to drive (text, val, …)
   * @param {'state'|'mapping'|'template'|'constant'|'switch'} [config.strategy]
   * @param {'entity'|'toggle'|'template'|'constant'} [config.source] — @deprecated legacy alias for `strategy`
   * @param {string} [config.entityId]  — state/mapping: HA entity ID
   * @param {'entity'|'template'} [config.sourceType] — legacy Designer source
   *   marker for state/mapping bindings. `template` is deprecated and is kept
   *   only so stale projects remain readable until the user selects an entity.
   * @param {'state'|'attribute'} [config.valueSource] — state binding source;
   *   defaults to 'state'. Legacy bindings with an attribute but no
   *   valueSource retain their old attribute behaviour.
   * @param {string} [config.attribute] — state/mapping: selected HA attribute
   * @param {string} [config.filter]    — state: Jinja2 filter chain, e.g. '| round(1)'
   * @param {string} [config.fallback]  — state/mapping: value when unavailable
   * @param {Array<{when: {operator: string, value: string}|null, result: string}>} [config.rules] — mapping: ordered rule list
   * @param {Array<{when: {operator: string, value: string}|null, binding: object}>} [config.cases] — switch: ordered cases
   * @param {string} [config.onValue]   — @deprecated legacy alias, folded into `rules[0]`
   * @param {string} [config.offValue]  — @deprecated legacy alias, folded into `rules[1]`
   * @param {string} [config.template]  — template strategy: Jinja2 expression
   * @param {string} [config.value]     — constant strategy: fixed value
   * @param {Array<{type: string, params: Object}>} [config.transforms] — value-transform
   *   pipeline, applied before strategy evaluation; see valueTransforms.js
   * @param {{type: string, params: Object}} [config.formatter] — presentation
   *   formatter, applied after transforms (state/aggregate only — mapping's
   *   output is always its matched rule's own literal); see valueFormatters.js.
   *   Always defaults to `{ type: 'none', params: {} }` — this class is
   *   domain-agnostic and never infers a formatter from entityId/attribute;
   *   historical-data compatibility mappings live in LegacyEventsToAst.js.
   */
  constructor({
    id,
    property = '',
    strategy,
    source,
    entityId = '',
    sourceType = 'entity',
    valueSource,
    attribute = '',
    filter = '',
    fallback = '',
    template = '',
    value = '',
    rules,
    cases = [],
    onValue,
    offValue,
    transforms = [],
    formatter = { type: 'none', params: {} },
  } = {}) {
    this.id = id ?? crypto.randomUUID();
    this.property = property;

    this.strategy = strategy
      ?? AutomationBinding.#LEGACY_SOURCE_TO_STRATEGY[source]
      ?? source
      ?? 'state';

    this.entityId = entityId;
    this.sourceType = sourceType === 'template' ? 'template' : 'entity';
    this.attribute = attribute;
    // Older saved bindings already used `attribute` directly. Treat those as
    // attribute bindings while making the new default explicit in memory.
    this.valueSource = valueSource ?? (attribute ? 'attribute' : 'state');
    this.filter = filter;
    this.fallback = fallback;
    this.template = template;
    this.value = value;
    this.transforms = transforms;
    this.formatter = formatter;

    this.cases = cases.map(item => ({
      when: item.when ? { ...item.when } : null,
      binding: item.binding instanceof AutomationBinding ? item.binding : new AutomationBinding({ property, ...item.binding }),
    }));

    this.rules = rules ?? (
      (onValue !== undefined || offValue !== undefined)
        ? [
            { when: { operator: '==', value: 'on' }, result: onValue ?? '' },
            { when: null, result: offValue ?? '' },
          ]
        : []
    );
  }

  /** @deprecated legacy alias for `strategy` — see StateBindingsTable.vue's Source column. */
  get source() {
    return AutomationBinding.#STRATEGY_TO_LEGACY_SOURCE[this.strategy] ?? this.strategy;
  }

  set source(v) {
    this.strategy = AutomationBinding.#LEGACY_SOURCE_TO_STRATEGY[v] ?? v;
  }

  /** @deprecated legacy alias for `rules[0].result` — see StateBindingsTable.vue's Toggle editor. */
  get onValue() {
    return this.rules[0]?.result ?? '';
  }

  set onValue(v) {
    this.#ensureToggleRules();
    this.rules[0].result = v;
  }

  /** @deprecated legacy alias for `rules[1].result` — see StateBindingsTable.vue's Toggle editor. */
  get offValue() {
    return this.rules[1]?.result ?? '';
  }

  set offValue(v) {
    this.#ensureToggleRules();
    this.rules[1].result = v;
  }

  /** Lazily gives `rules` the two-entry on/off shape the legacy accessors read/write. */
  #ensureToggleRules() {
    if (this.rules.length < 2) {
      this.rules = [
        { when: { operator: '==', value: 'on' }, result: this.rules[0]?.result ?? '' },
        { when: null, result: this.rules[1]?.result ?? '' },
      ];
    }
  }

  toJSON() {
    const out = { id: this.id, property: this.property, strategy: this.strategy };
    switch (this.strategy) {
      case 'template':
        out.template = this.template;
        break;
      case 'constant':
        out.value = this.value;
        break;
      case 'switch':
        out.entityId = this.entityId;
        if (this.attribute) out.attribute = this.attribute;
        out.cases = this.cases.map(item => ({ when: item.when, binding: item.binding.toJSON() }));
        break;
      case 'mapping':
        if (this.sourceType === 'template') out.sourceType = 'template';
        out.entityId = this.entityId;
        if (this.attribute) out.attribute = this.attribute;
        if (this.fallback) out.fallback = this.fallback;
        out.rules = this.rules;
        break;
      default: // 'state'
        if (this.sourceType === 'template') out.sourceType = 'template';
        out.entityId = this.entityId;
        if (this.valueSource === 'attribute') {
          out.valueSource = 'attribute';
          if (this.attribute) out.attribute = this.attribute;
        }
        if (this.filter) out.filter = this.filter;
        if (this.fallback) out.fallback = this.fallback;
    }
    if (this.transforms.length > 0) out.transforms = this.transforms;
    if (this.formatter.type !== 'none') out.formatter = this.formatter;
    return out;
  }

  static fromJSON(data) {
    if (!data) return null;
    return new AutomationBinding(data);
  }
}
